import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, RotateCcw, Timer, ShieldCheck, BadgePercent, PhoneCall } from "lucide-react";
import { SITE, IMAGES } from "@/config/siteConfig";
import { waLink, trackEvent } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const STEPS = [
  {
    id: "fournisseur",
    question: "Quel est votre fournisseur internet actuel ?",
    subtitle: "Démarrez le questionnaire en sélectionnant une option.",
    type: "single",
    options: ["Bell", "Vidéotron", "Cogeco", "Autre fournisseur"],
  },
  {
    id: "services",
    question: "Quels services vous intéressent ?",
    subtitle: "Sélectionnez tout ce qui s'applique.",
    type: "multi",
    options: ["Internet", "Mobile", "Télévision", "Téléphonie résidentielle"],
  },
  {
    id: "budget",
    question: "Combien payez-vous par mois actuellement ?",
    subtitle: "Une estimation suffit — ça nous aide à vous trouver mieux.",
    type: "single",
    options: ["Moins de 50 $", "50 $ à 80 $", "80 $ à 120 $", "Plus de 120 $"],
  },
  {
    id: "codepostal",
    question: "Quel est votre code postal ?",
    subtitle: "Les promotions et rabais varient selon votre secteur.",
    type: "text",
    placeholder: "Ex : H2N 2J9",
  },
];

const TRUST = [
  { icon: Timer, label: "30 secondes" },
  { icon: BadgePercent, label: "100 % gratuit" },
  { icon: ShieldCheck, label: "Sans engagement" },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ fournisseur: "", services: [], budget: "", codepostal: "" });
  const [started] = useState(true);

  useEffect(() => {
    trackEvent("quiz_debut", { source: "quiz" });
  }, []);

  const current = STEPS[step];
  const done = step >= STEPS.length;

  const choose = (value) => {
    if (current.type === "single") {
      setAnswers((a) => ({ ...a, [current.id]: value }));
      trackEvent("quiz_etape", { etape: current.id, reponse: value });
      setTimeout(() => setStep((s) => s + 1), 220);
    } else {
      setAnswers((a) => {
        const list = a.services.includes(value)
          ? a.services.filter((v) => v !== value)
          : [...a.services, value];
        return { ...a, services: list };
      });
    }
  };

  const canContinue = current?.type === "multi"
    ? answers.services.length > 0
    : current?.type === "text"
      ? answers.codepostal.trim().length >= 6
      : true;

  const waMessage = useMemo(() => {
    const services = answers.services.length ? answers.services.join(", ") : "À discuter";
    return `Bonjour, je viens de votre site web (questionnaire en ligne).\n\n• Fournisseur internet actuel : ${answers.fournisseur}\n• Services qui m'intéressent : ${services}\n• Budget mensuel actuel : ${answers.budget}\n• Code postal : ${answers.codepostal}\n\nJ'aimerais connaître les meilleures offres et promotions disponibles dans ma région.`;
  }, [answers]);

  const restart = () => {
    setAnswers({ fournisseur: "", services: [], budget: "", codepostal: "" });
    setStep(0);
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 font-sans text-black antialiased" data-testid="quiz-page">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-2xl items-center justify-between px-4">
          <Link to="/" data-testid="quiz-logo-link" aria-label="Retour à l'accueil">
            <span className="flex h-10 items-center overflow-hidden rounded-md bg-black px-2">
              <img src={IMAGES.logo} alt={`Logo ${SITE.companyName}`} className="h-7 w-auto object-contain" />
            </span>
          </Link>
          <Link to="/" data-testid="quiz-back-home" className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition-colors duration-300 hover:text-black">
            <ArrowLeft className="h-4 w-4" /> Accueil
          </Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-10 md:py-16">
        {!done && (
          <div className="mb-9 text-center">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-gray-500">
              <span className="h-2 w-2 rounded-full bg-jeem-yellow" /> Questionnaire express
            </span>
            <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-black sm:text-4xl" data-testid="quiz-headline">
              Payez-vous trop cher vos services télécom ?
            </h1>
            <p className="mx-auto mt-3 max-w-md text-base text-gray-600">
              Certaines promotions ne sont pas affichées en ligne et varient selon votre secteur. Répondez à 4 questions — un conseiller vous montre les meilleures options sur WhatsApp.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-medium text-gray-500">
              {TRUST.map((t) => (
                <span key={t.label} className="flex items-center gap-1.5"><t.icon className="h-4 w-4 text-black" /> {t.label}</span>
              ))}
            </div>
          </div>
        )}
        {!started ? null : (
          <>
            <div className="mb-10">
              <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-gray-400">
                <span data-testid="quiz-progress-label">Étape {Math.min(step + 1, STEPS.length)} sur {STEPS.length}</span>
                <span>{Math.round((Math.min(step, STEPS.length) / STEPS.length) * 100)} %</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <motion.div
                  className="h-full rounded-full bg-jeem-yellow"
                  animate={{ width: `${(Math.min(step, STEPS.length) / STEPS.length) * 100}%` }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="quiz-progress-bar"
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h2 className="text-center font-display text-2xl font-extrabold tracking-tight text-black sm:text-3xl" data-testid={`quiz-question-${current.id}`}>
                    {current.question}
                  </h2>
                  <p className="mt-3 text-center text-base text-gray-500">{current.subtitle}</p>

                  {current.type !== "text" ? (
                    <div className="mt-9 space-y-3.5">
                      {current.options.map((opt) => {
                        const selected = current.type === "multi" && answers.services.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => choose(opt)}
                            data-testid={`quiz-option-${current.id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`flex min-h-[60px] w-full items-center justify-between rounded-2xl border-2 px-6 py-4 text-lg font-bold transition-colors duration-300 ${
                              selected
                                ? "border-jeem-yellow bg-jeem-yellow/20 text-black"
                                : "border-gray-200 bg-white text-gray-800 hover:border-jeem-yellow"
                            }`}
                          >
                            {opt}
                            {current.type === "multi" && (
                              <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${selected ? "border-black bg-jeem-yellow" : "border-gray-300"}`}>
                                {selected && <Check className="h-4 w-4 text-black" strokeWidth={3} />}
                              </span>
                            )}
                          </button>
                        );
                      })}
                      {current.type === "multi" && (
                        <button
                          type="button"
                          onClick={() => { trackEvent("quiz_etape", { etape: "services", reponse: answers.services.join(",") }); setStep((s) => s + 1); }}
                          disabled={!canContinue}
                          data-testid="quiz-next-services"
                          className="mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-black text-base font-bold text-white transition-colors duration-300 hover:bg-gray-800 disabled:opacity-40"
                        >
                          Continuer <ArrowRight className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <form
                      className="mx-auto mt-9 max-w-md"
                      onSubmit={(e) => { e.preventDefault(); if (canContinue) { trackEvent("quiz_etape", { etape: "codepostal", reponse: answers.codepostal }); setStep((s) => s + 1); } }}
                    >
                      <input
                        type="text"
                        value={answers.codepostal}
                        onChange={(e) => setAnswers((a) => ({ ...a, codepostal: e.target.value.toUpperCase() }))}
                        placeholder={current.placeholder}
                        data-testid="quiz-codepostal-input"
                        maxLength={7}
                        autoComplete="postal-code"
                        className="h-16 w-full rounded-2xl border-2 border-gray-200 bg-white px-6 text-center font-display text-xl font-bold tracking-widest text-black outline-none transition-colors duration-300 focus:border-jeem-yellow"
                      />
                      <button
                        type="submit"
                        disabled={!canContinue}
                        data-testid="quiz-next-codepostal"
                        className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-black text-base font-bold text-white transition-colors duration-300 hover:bg-gray-800 disabled:opacity-40"
                      >
                        Voir mes résultats <ArrowRight className="h-5 w-5" />
                      </button>
                    </form>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-center text-center"
                  data-testid="quiz-result"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-jeem-yellow">
                    <Check className="h-8 w-8 text-black" strokeWidth={3} />
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-black sm:text-3xl">
                    Parfait, on a tout ce qu'il faut !
                  </h2>
                  <p className="mt-3 max-w-md text-base text-gray-600">
                    Envoyez vos réponses à un conseiller sur WhatsApp. Il vérifiera les promotions et les meilleurs prix disponibles dans votre secteur.
                  </p>
                  <p className="mt-4 flex max-w-md items-center justify-center gap-2 rounded-2xl bg-jeem-yellow/20 px-4 py-3 text-sm font-bold text-black" data-testid="quiz-urgency-note">
                    <Timer className="h-4 w-4 shrink-0" />
                    Les offres de votre secteur changent rapidement — un conseiller vous répond en quelques minutes pendant les heures d'ouverture.
                  </p>

                  <div className="mt-7 w-full max-w-md space-y-2.5 rounded-3xl border border-gray-200 bg-white p-6 text-left" data-testid="quiz-summary">
                    <p className="text-sm text-gray-700"><span className="font-bold text-black">Fournisseur actuel :</span> {answers.fournisseur}</p>
                    <p className="text-sm text-gray-700"><span className="font-bold text-black">Services souhaités :</span> {answers.services.join(", ") || "À discuter"}</p>
                    <p className="text-sm text-gray-700"><span className="font-bold text-black">Budget actuel :</span> {answers.budget}</p>
                    <p className="text-sm text-gray-700"><span className="font-bold text-black">Code postal :</span> {answers.codepostal}</p>
                  </div>

                  <a
                    href={waLink(waMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("quiz_termine_whatsapp", { fournisseur: answers.fournisseur, codepostal: answers.codepostal })}
                    data-testid="quiz-whatsapp-cta"
                    className="mt-7 flex h-15 min-h-[60px] w-full max-w-md items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 text-lg font-bold text-white shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95"
                  >
                    <WhatsAppIcon className="h-6 w-6" />
                    Voir mes offres sur WhatsApp
                  </a>
                  <p className="mt-4 text-xs text-gray-400">Gratuit et sans engagement. Vos réponses sont déjà incluses dans le message.</p>

                  <a
                    href={`tel:${SITE.phone.replace(/[^+\d]/g, "")}`}
                    onClick={() => trackEvent("clic_telephone", { source: "quiz" })}
                    data-testid="quiz-call-cta"
                    className="mt-4 flex items-center gap-2 font-display text-base font-bold text-black underline decoration-jeem-yellow decoration-4 underline-offset-4 transition-colors duration-300 hover:text-gray-600"
                  >
                    <PhoneCall className="h-5 w-5" />
                    Vous préférez parler ? Appelez le {SITE.phone}
                  </a>

                  <button
                    type="button"
                    onClick={restart}
                    data-testid="quiz-restart-button"
                    className="mt-6 flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors duration-300 hover:text-black"
                  >
                    <RotateCcw className="h-4 w-4" /> Recommencer
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </main>

      <footer className="border-t border-black/5 bg-white py-6">
        <p className="text-center text-xs text-gray-400">
          © {new Date().getFullYear()} {SITE.companyName} • {SITE.address}
        </p>
      </footer>
    </div>
  );
}
